import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  CircularProgress,
  Alert,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PlagerismIcon from '@mui/icons-material/Plagiarism';
import PageHeader from '../components/PageHeader';
import { useAssignmentService } from '../services/assignmentService';
import { useNavigate } from 'react-router-dom';
import { useAssessmentService } from '../services/assessmentService';

type Assignment = {
  id: number;
  scenario_id: number;
  title: string;
  user_email: string;
  assigned_at?: string;
  start_date?: string;
  end_date: string;
  status?: string;
  completed_on?: string;
  feedback_id?: string;
  transcript_id?: number;
  completed_at?: string;
  score?: string;
};

const PAGE_SIZE = 25; 
const CENTRAL_TIMEZONE = 'America/Chicago';

function parseApiTimestamp(timestamp?: string): Date | null {
  if (!timestamp) {
    return null;
  }

  const trimmed = timestamp.trim();
  if (!trimmed) {
    return null;
  }

  const normalized = trimmed.includes(' ') && !trimmed.includes('T')
    ? trimmed.replace(' ', 'T')
    : trimmed;

  const timezoneMatch = /(Z|[+-]\d{2}:?\d{2})$/i.exec(normalized);
  const timezoneSuffix = timezoneMatch?.[0] ?? '';
  const withoutTimezone = timezoneSuffix
    ? normalized.slice(0, -timezoneSuffix.length)
    : normalized;

  // Handles: YYYY-MM-DDTHH:mm:ss(.ffffff)?
  const baseMatch = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?$/.exec(withoutTimezone);
  if (!baseMatch) {
    const fallback = new Date(normalized);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  const [, year, month, day, hour, minute, second, fractional = ''] = baseMatch;
  const milliseconds = Number((fractional + '000').slice(0, 3));

  // No timezone in payload is treated as UTC for deterministic behavior.
  if (!timezoneSuffix) {
    return new Date(Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      milliseconds,
    ));
  }

  // Normalize offsets like -0500 -> -05:00 for cross-browser parsing.
  const normalizedTz = timezoneSuffix !== 'Z' && timezoneSuffix.length === 5
    ? `${timezoneSuffix.slice(0, 3)}:${timezoneSuffix.slice(3)}`
    : timezoneSuffix;
  const withTimezone = `${year}-${month}-${day}T${hour}:${minute}:${second}.${String(milliseconds).padStart(3, '0')}${normalizedTz}`;
  const parsed = new Date(withTimezone);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateOnly(timestamp?: string): string {
  const parsed = parseApiTimestamp(timestamp);
  if (!parsed) {
    return '—';
  }

  return parsed.toLocaleDateString(undefined, { timeZone: CENTRAL_TIMEZONE });
}

function formatDateTime(timestamp?: string): string {
  const parsed = parseApiTimestamp(timestamp);
  if (!parsed) {
    return '-';
  }

  return parsed.toLocaleString(undefined, { timeZone: CENTRAL_TIMEZONE });
}

const AdminMasterAssignmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const assignmentService = useAssignmentService();

  const [currentAssignments, setCurrentAssignments] = useState<Assignment[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsError, setAssignmentsError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [showUserEmailSearch, setShowUserEmailSearch] = useState(false);
  const [userEmailInput, setUserEmailInput] = useState('');
  const [userEmailFilter, setUserEmailFilter] = useState('');

  const [showStatusSearch, setShowStatusSearch] = useState(false);
  const [statusInput, setStatusInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [showScenarioSearch, setShowScenarioSearch] = useState(false);
  const [scenarioInput, setScenarioInput] = useState('');
  const [scenarioFilter, setScenarioFilter] = useState('');
  const [scenarioOptions, setScenarioOptions] = useState<{ id: number; title: string }[]>([]);

  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    setAssignmentsLoading(true);
    setAssignmentsError(null);

    assignmentService.getAssignments(page, { 
      user_email: userEmailFilter,
      status: statusFilter,
      roleplay_id: scenarioFilter,
    }, sortBy, sortOrder)
      .then((res: { assignments: Assignment[]; total: number }) => {
        setCurrentAssignments(res.assignments);
        setTotalPages(Math.ceil(res.total / PAGE_SIZE));
      })
      .catch(err => setAssignmentsError(err.message || "Failed to load assignments"))
      .finally(() => setAssignmentsLoading(false));
  }, [page, userEmailFilter, scenarioFilter, statusFilter, sortBy, sortOrder]);

  const assessmentService = useAssessmentService();

  const handleExport = async (format: 'csv' | 'xlsx') => {
    try {
      const response = await assignmentService.exportAssignments(
        {
          user_email: userEmailFilter,
          status: statusFilter,
          roleplay_id: scenarioFilter,
        },
        format,
        sortBy,
        sortOrder
      );
      const blob = await response.blob();
      let filename = format === 'csv' ? 'assignments.csv' : 'assignments.xlsx';
      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition) {
        const match = /filename="?([^"]+)"?/.exec(contentDisposition);
        if (match) filename = match[1];
      }
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      globalThis.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export assignments');
    }
  };

  const handleDownloadTranscript = async (transcriptId: number) => {
    try {
      const response = await assignmentService.downloadTranscriptResponse(transcriptId);
      const disposition = response.headers.get("Content-Disposition");

      // Fail if no content-disposition or filename
      if (!disposition) {
        alert("No Content-Disposition header found. Cannot download transcript.");
        return;
      }

      // Regex for filename (handles quotes and non-quotes, as well as UTF-8 filenames)
      let filename: string | null = null;
      const filenameRegex = /filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i;
      const match = disposition.match(filenameRegex);
      if (match && match[1]) {
        filename = decodeURIComponent(match[1]);
      }

      if (!filename) {
        alert("Could not determine transcript filename from server response.");
        return;
      }

      const blob = await response.blob();
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Transcript download failed " + transcriptId);
    }
  };

  useEffect(() => {
    assessmentService.fetchAllAssessmentNames()
      .then((data: { id: number; title: string }[]) => setScenarioOptions(data))
      .catch(() => setScenarioOptions([]));
  }, []);

  function handleBack(): void {
    navigate(-1);
  }

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      py: 8,
      p: 4,
      height: '100vh',
      overflowY: 'auto'
    }}>
      <Box sx={{
        width: { xs: '95%', md: '95%', lg: '95%', xl: '85%' },
        p: 4,
        borderRadius: 3,
      }}>
        <PageHeader
          title="Assignments"
          subtitle="Review feedback from previous roleplay sessions"
          onBack={handleBack}
          showBackButton={true}
        />

        {assignmentsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {assignmentsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {assignmentsError}
          </Alert>
        )}

        {!assignmentsLoading && !assignmentsError && (
          <>
            <TableContainer component={Paper}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      onClick={() => setShowUserEmailSearch(true)}
                      sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      User Email
                    </TableCell>
                    <TableCell
                      onClick={() => setShowScenarioSearch(true)}
                      sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      Roleplay
                    </TableCell>
                    <TableCell
                      onClick={() => setShowStatusSearch(true)}
                      sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                      onClick={() => {
                        if (sortBy === 'assigned_at') {
                          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortBy('assigned_at');
                          setSortOrder('desc');
                        }
                      }}
                    >
                      Assigned At {sortBy === 'assigned_at' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                    </TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                      onClick={() => {
                        if (sortBy === 'start_date') {
                          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortBy('start_date');
                          setSortOrder('desc');
                        }
                      }}
                    >
                      Start Date {sortBy === 'start_date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                    </TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                      onClick={() => {
                        if (sortBy === 'end_date') {
                          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortBy('end_date');
                          setSortOrder('desc');
                        }
                      }}
                    >
                      Due At {sortBy === 'end_date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                    </TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                      onClick={() => {
                        if (sortBy === 'completed_at') {
                          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortBy('completed_at');
                          setSortOrder('desc');
                        }
                      }}
                    >
                      Completed On {sortBy === 'completed_at' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                    </TableCell>
                    <TableCell sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                      Overall Score
                    </TableCell>
                    <TableCell sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                      Results and Feedback
                    </TableCell>
                    <TableCell sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                      Transcript
                    </TableCell>
                  </TableRow>
                  {showUserEmailSearch && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={userEmailInput}
                            onChange={e => setUserEmailInput(e.target.value)}
                            placeholder="Search user email..."
                            style={{ marginRight: 8, padding: 4, flex: 1 }}
                            autoFocus
                          />
                          <button
                            onClick={() => {
                              setUserEmailFilter(userEmailInput);
                              setShowUserEmailSearch(false);
                              setPage(1);
                            }}
                            style={{ marginRight: 8 }}
                          >
                            Search
                          </button>
                          <button
                            onClick={() => {
                              setUserEmailFilter('');
                              setUserEmailInput('');
                              setShowUserEmailSearch(false);
                              setPage(1);
                            }}
                          >
                            Clear
                          </button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {showScenarioSearch && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <select
                            value={scenarioInput}
                            onChange={e => setScenarioInput(e.target.value)}
                            style={{ marginRight: 8, padding: 4 }}
                            autoFocus
                          >
                            <option value="">All scenarios</option>
                            {scenarioOptions.map(option => (
                              <option key={option.id} value={option.id}>{option.title}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => {
                              setScenarioFilter(scenarioInput);
                              setShowScenarioSearch(false);
                              setPage(1);
                            }}
                            style={{ marginRight: 8 }}
                          >
                            Search
                          </button>
                          <button
                            onClick={() => {
                              setScenarioFilter('');
                              setScenarioInput('');
                              setShowScenarioSearch(false);
                              setPage(1);
                            }}
                          >
                            Clear
                          </button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {showStatusSearch && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <select
                            value={statusInput}
                            onChange={e => setStatusInput(e.target.value)}
                            style={{ marginRight: 8, padding: 4 }}
                            autoFocus
                          >
                            <option value="">All statuses</option>
                            <option value="assigned">Assigned</option>
                            <option value="completed">Completed</option>
                          </select>
                          <button
                            onClick={() => {
                              setStatusFilter(statusInput);
                              setShowStatusSearch(false);
                              setPage(1);
                            }}
                            style={{ marginRight: 8 }}
                          >
                            Search
                          </button>
                          <button
                            onClick={() => {
                              setStatusFilter('');
                              setStatusInput('');
                              setShowStatusSearch(false);
                              setPage(1);
                            }}
                          >
                            Clear
                          </button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableHead>
                <TableBody>
                  {currentAssignments.map((a, idx) => (
                    <TableRow key={`${a.id}-${idx}`}>
                      <TableCell>{a.user_email}</TableCell>
                      <TableCell>{a.title}</TableCell>
                      <TableCell>{a.status || 'assigned'}</TableCell>
                      <TableCell>
                        {formatDateOnly(a.assigned_at)}
                      </TableCell>
                      <TableCell>{formatDateOnly(a.start_date)}</TableCell>
                      <TableCell>
                        {formatDateOnly(a.end_date)}
                      </TableCell>
                      <TableCell>
                        {formatDateTime(a.completed_at)}
                      </TableCell>
                      <TableCell>
                        {a.score}
                      </TableCell>
                      <TableCell>
                        { a.feedback_id? (
                        <a href={`/feedback/${a.feedback_id}`}>
                        <Button
                          key={a.feedback_id}
                          startIcon={<PlagerismIcon />}
                          onClick={() => 
                            navigate(`/feedback/${a.feedback_id}`)
                          }
                          size="small"
                          color="primary"
                          sx={{ m: 0.5 }}
                        >
                        </Button>
                        </a>
                        ) :
                        '-'}
                      </TableCell>
                      <TableCell>
                        { a.transcript_id? (
                        <Button
                          key={a.transcript_id}
                          startIcon={<AssignmentIcon />}
                          onClick={() => 
                            handleDownloadTranscript(a.transcript_id!)
                          }
                          size="small"
                          color="primary"
                          sx={{ m: 0.5 }}
                        >
                        </Button>) :
                         '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}
            />
            <Button onClick={() => handleExport('csv')}>Export CSV</Button>
            <Button onClick={() => handleExport('xlsx')}>Export XLSX</Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default AdminMasterAssignmentsPage;